/* eslint-disable react-refresh/only-export-components -- activation registry pattern: components are referenced via exported config object */
import { Mail } from 'lucide-react';

import type { RenderedMessage, WorkflowActionConfigSafe } from '../../types';
import { Badge } from '../ui/badge';
import type { IntegrationActivation } from './types';

function EmailPreviewMessage({ message }: { message: RenderedMessage }): React.ReactElement | null {
  if (message.type !== 'resend') return null;
  return (
    <div className="rounded-md border border-border bg-card p-3 text-sm">
      <div className="mb-1 text-xs text-muted-foreground">
        To: <span className="font-medium text-foreground">{message.emailTo}</span>
      </div>
      <div className="mb-2 font-medium">{message.emailSubject}</div>
      {/* Body is rendered as plain text for safety — the backend HTML-escapes template values */}
      <pre className="text-xs whitespace-pre-wrap text-muted-foreground">{message.emailBody}</pre>
    </div>
  );
}

function EmailConfigSummary({ config }: { config: WorkflowActionConfigSafe }): React.ReactElement {
  return (
    <div className="space-y-2 text-sm">
      <div className="flex items-center gap-2">
        <Mail size={14} className="text-muted-foreground" />
        <span className="font-medium">Email (Resend)</span>
        <Badge variant="outline" className="text-[0.625rem]">
          {config.mode === 'per_row' ? 'Customer-facing' : 'Internal'}
        </Badge>
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        {config.toColumn && (
          <div>
            <p className="text-muted-foreground">Recipient Column</p>
            <p className="font-mono">{config.toColumn}</p>
          </div>
        )}
        {config.subjectTemplate && (
          <div>
            <p className="text-muted-foreground">Subject</p>
            <p className="truncate">{config.subjectTemplate}</p>
          </div>
        )}
      </div>
      {config.bodyTemplate && (
        <div>
          <p className="mb-1 text-xs text-muted-foreground">Body Template</p>
          <pre className="line-clamp-3 overflow-x-auto rounded-md bg-muted p-2 text-xs">{config.bodyTemplate}</pre>
        </div>
      )}
    </div>
  );
}

export const resendEmailActivation: IntegrationActivation = {
  type: 'resend',
  label: 'Email (Resend)',
  icon: Mail,
  PreviewMessage: EmailPreviewMessage,
  ConfigSummary: EmailConfigSummary,
};
